/**
 * Parameters the embedding host page (m-denture.zangesa.com) may hand over to
 * the MDenture iframe.
 *
 * SECURITY: this is an explicit allowlist. Anything the host sends that is not
 * listed here is dropped, so a crafted URL or a rogue postMessage can never
 * inject arbitrary state into the app. Values are also length-capped to keep a
 * hostile parent from bloating memory or the DOM.
 */
export const EMBED_PARAM_KEYS = [
  'lang',
  'source',
  'partnerId',
  'returnUrl',
  'productionCenterId',
  'companyId',
  'contactId',
] as const;

export type EmbedParamKey = (typeof EMBED_PARAM_KEYS)[number];

export type EmbedParams = Partial<Record<EmbedParamKey, string>>;

const MAX_VALUE_LENGTH = 256;

function isAllowedKey(key: string): key is EmbedParamKey {
  return (EMBED_PARAM_KEYS as readonly string[]).includes(key);
}

/** Picks the allowlisted embed parameters out of an arbitrary record. */
export function pickEmbedParams(source: Record<string, unknown>): EmbedParams {
  const params: EmbedParams = {};
  for (const [key, value] of Object.entries(source)) {
    if (!isAllowedKey(key) || typeof value !== 'string') {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed && trimmed.length <= MAX_VALUE_LENGTH) {
      params[key] = trimmed;
    }
  }
  return params;
}

/** Reads the allowlisted embed parameters from a URL query string. */
export function readEmbedParams(search: string): EmbedParams {
  const query = new URLSearchParams(search);
  const raw: Record<string, string> = {};
  for (const key of EMBED_PARAM_KEYS) {
    const value = query.get(key);
    if (value !== null) {
      raw[key] = value;
    }
  }
  return pickEmbedParams(raw);
}

/** True when the app is running inside an iframe. */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — which itself proves we are framed.
    return true;
  }
}
